import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/shared/utils/helpers'

const buttonVariants = cva(
	'inline-flex text-primary items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
	{
		variants: {
			variant: {
				default: 'bg-primary text-secondary hover:bg-primary/90',
				destructive:
					'bg-destructive text-destructive-foreground hover:bg-destructive/90',
				outline:
					'border bg-outline hover:bg-accent hover:text-accent-foreground',
				secondary:
					'bg-secondary text-secondary-foreground hover:bg-secondary/80',
				ghost:
					'hover:bg-accent hover:text-accent-foreground text-primary-foreground',
				link: 'text-primary underline-offset-4 hover:underline',
				action: 'text-xs/1 bg-primary text-secondary hover:bg-primary/90',
				noXPadding: 'bg-primary text-secondary hover:bg-primary/90 px-4',
			},
			size: {
				default: 'h-10 px-4 py-2',
				sm: 'h-9 rounded-md px-3',
				lg: 'h-11 rounded-md px-8',
				icon: 'h-10 w-10',
				action: 'py-2.5 px-4 rounded-full h-8 text-xs',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
		compoundVariants: [
			{ variant: 'noXPadding', className: '!w-fit !md:w-fit' },
		],
	}
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, variant, size, asChild = false, disabled = false, ...props },
		ref
	) => {
		const Comp = asChild ? Slot : 'button'
		// Add cursor pointer to the button by default
		className = cn(className, 'cursor-pointer')

		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				disabled={disabled}
				ref={ref}
				{...props}
			/>
		)
	}
)
Button.displayName = 'Button'

export { Button, buttonVariants }
